<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? &quot;Help & Support - Raised Tickets&quot; : &quot;Deleted Raise Tickets&quot;"
              :subtitle="isDeletePage() != true ? &quot;View and respond to complaints and support tickets raised by users&quot; : &quot;Restore or permanently delete raise tickets&quot;"
            >
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Tickets</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Tickets"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                    placeholder="Search by title, description or user..."
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.status"
                    :items="statuses"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    outlined
                    dense
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-progress-check"
                    @input="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Show Deleted Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.show_deleted"
                    :items="deletedStatuses"
                    item-text="text"
                    item-value="value"
                    label="View Type"
                    outlined
                    dense
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-eye"
                    @input="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Export Button -->
                <v-col cols="12" md="2" class="d-flex align-center">
                  <v-btn
                    color="success"
                    outlined
                    small
                    @click="generateExcel()"
                    :loading="isExcelloading"
                    :disabled="!raiseTickets.length || isDeletePage()"
                    class="px-2"
                  >
                    <v-icon small>mdi-file-excel</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-text class="px-0 py-0">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-12">
                <health-loader message="Loading raise tickets..." />
              </div>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="raiseTickets"
                :items-per-page="-1"
                hide-default-footer
                class="elevation-0"
                :class="{ 'empty-table': !raiseTickets.length }"
              >
                <template v-slot:[`item.sr_no`]="{ index }">
                  <span class="text-body-2">{{
                    (page - 1) * ticketsPerPage + index + 1
                  }}</span>
                </template>

                <template v-slot:[`item.title`]="{ item }">
                  <div
                    class="text-body-2 font-weight-medium text-truncate"
                    style="max-width: 320px"
                  >
                    {{ item.title || "(No title)" }}
                  </div>
                  <div
                    v-if="item.latest_message"
                    class="text-caption grey--text text-truncate"
                    style="max-width: 320px"
                  >
                    {{ item.latest_message }}
                  </div>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                  <v-chip
                    small
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    class="font-weight-medium status-chip"
                  >
                    <v-icon left small>{{ getStatusIcon(item.status) }}</v-icon>
                    {{ item.status_text }}
                  </v-chip>
                </template>

                <template v-slot:[`item.created_by`]="{ item }">
                  <span class="text-body-2">
                    {{ item.created_by_name || "N/A" }}
                  </span>
                </template>

                <template v-slot:[`item.users_count`]="{ item }">
                  <v-chip small outlined color="primary">
                    <v-icon left x-small>mdi-account-multiple</v-icon>
                    {{ item.users_count || 0 }}
                  </v-chip>
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                  <span class="text-body-2">{{
                    formatDate(item.created_at)
                  }}</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="d-flex justify-end align-center">
                    <template v-if="!isDeletePage()">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            :to="`/raise-tickets/${encryptIt(item.id)}`"
                            icon
                            small
                            color="primary"
                            class="mr-1 action-btn"
                          >
                            <v-icon small>mdi-eye-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>View ticket</span>
                      </v-tooltip>

                      <v-tooltip top v-if="canDelete('raise_tickets')">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            @click="openDeleteDialog(item)"
                            icon
                            small
                            color="error"
                            class="action-btn"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete ticket</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            @click="Restore_this(item.id)"
                            icon
                            small
                            color="success"
                            class="action-btn"
                          >
                            <v-icon small>mdi-delete-restore</v-icon>
                          </v-btn>
                        </template>
                        <span>Restore ticket</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <template v-slot:no-data>
                  <empty-state icon="mdi-lifebuoy" :title="isDeletePage() ? &quot;No deleted raise tickets found&quot; : &quot;No raise tickets available&quot;" :message="isDeletePage() ? &quot;All raise tickets are currently active&quot; : &quot;Tickets raised by users will show up here&quot;" />
                </template>
              </v-data-table>

              <!-- Pagination -->
              <div
                v-if="raiseTickets.length && paginationLength > 1"
                class="px-4 py-3"
              >
                <v-divider class="mb-3"></v-divider>
                <div class="d-flex align-center justify-space-between">
                  <div class="text-body-2 grey--text">
                    Showing {{ (page - 1) * ticketsPerPage + 1 }} to
                    {{ Math.min(page * ticketsPerPage, count) }} of
                    {{ count }} entries
                  </div>
                  <v-pagination
                    v-model="page"
                    :length="paginationLength"
                    :total-visible="7"
                    circle
                    color="primary"
                    @input="getData()"
                  ></v-pagination>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <DeleteConfirmationDialog
        v-model="deleteDialog"
        :item="itemToDelete"
        :loading="deleteLoading"
        @confirm="confirmDelete"
        @cancel="deleteDialog = false"
      />
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";

export default {
  name: "RaiseTicketsListPage",
  components: { DeleteConfirmationDialog },
  data() {
    return {
      raiseTickets: [],
      isLoading: true,
      isExcelloading: false,
      page: 1,
      count: 0,
      ticketsPerPage: 10,
      paginationLength: 0,
      deleteDialog: false,
      itemToDelete: null,
      deleteLoading: false,
      headers: [
        { text: "Sr No", value: "sr_no", width: "70px", sortable: false },
        { text: "Ticket", value: "title", sortable: false },
        { text: "Status", value: "status", width: "140px", sortable: false },
        {
          text: "Raised By",
          value: "created_by",
          width: "150px",
          sortable: false,
        },
        {
          text: "Members",
          value: "users_count",
          width: "110px",
          sortable: false,
        },
        {
          text: "Raised On",
          value: "created_at",
          width: "150px",
          sortable: false,
        },
        {
          text: "Actions",
          value: "actions",
          width: "100px",
          sortable: false,
          align: "end",
        },
      ],
      filter: {
        search_keyword: null,
        status: null,
        show_deleted: null,
      },
      statuses: [
        { text: "Pending", value: 0 },
        { text: "In Process", value: 1 },
        { text: "Completed", value: 2 },
        { text: "Rejected", value: 3 },
      ],
      deletedStatuses: [
        { text: "Active Tickets", value: false },
        { text: "Deleted Tickets", value: true },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.status !== null ||
        this.filter.show_deleted
      );
    },
  },

  mounted() {
    this.getData();
  },

  methods: {
    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.status !== null && this.filter.status !== "") count++;
      if (this.filter.show_deleted) count++;
      return count;
    },

    clearAllFilters() {
      this.filter.search_keyword = null;
      this.filter.status = null;
      this.filter.show_deleted = null;
      this.page = 1;
      this.getData();
    },

    async getData() {
      this.isLoading = true;

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.ticketsPerPage +
        "&context=panel" +
        "&show_deleted=" +
        (this.filter.show_deleted || false) +
        "&status=" +
        (this.filter.status ?? "") +
        "&search_keyword=" +
        (this.filter.search_keyword || "");

      await axios
        .get(`raise_tickets?${query}`)
        .then((response) => {
          this.raiseTickets = response.data.data.data || response.data.data;
          this.count = response.data.count;
          this.paginationLength = Math.ceil(this.count / this.ticketsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },

    openDeleteDialog(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleteLoading = true;
      try {
        await axios.post(
          `/raise_tickets/delete/${this.encryptIt(this.itemToDelete.id)}`
        );
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.getData();
      } catch (error) {
        console.log(error);
      }
      this.deleteLoading = false;
    },

    async Restore_this(id) {
      await axios
        .post(`/raise_tickets/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    async generateExcel() {
      this.isExcelloading = true;

      let query =
        "show_deleted=" +
        (this.filter.show_deleted || false) +
        "&status=" +
        (this.filter.status ?? "") +
        "&search_keyword=" +
        (this.filter.search_keyword || "");

      await axios
        .get(`raise_tickets/excel?${query}`, { responseType: "blob" })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          const fileName = `raise_tickets_export_${
            new Date().toISOString().split("T")[0]
          }.xlsx`;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isExcelloading = false;
    },

    isDeletePage() {
      return this.filter.show_deleted === true;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },

    getStatusColor(status) {
      switch (status) {
        case 0:
          return "orange";
        case 1:
          return "blue";
        case 2:
          return "green";
        case 3:
          return "red";
        default:
          return "grey";
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case 0:
          return "mdi-clock-outline";
        case 1:
          return "mdi-progress-clock";
        case 2:
          return "mdi-check-circle";
        case 3:
          return "mdi-close-circle";
        default:
          return "mdi-help-circle";
      }
    },
  },
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 0;
}

.v-data-table >>> thead th {
  background-color: #f8f9fa !important;
  border-bottom: 1px solid #dee2e6 !important;
  color: #495057 !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  padding: 12px 16px !important;
}

.v-data-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.v-data-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
}

.v-data-table >>> tbody td {
  padding: 12px 16px !important;
  font-size: 0.875rem;
}

.empty-table {
  min-height: 400px;
}

.v-card-subtitle {
  border-bottom: 1px solid #f0f0f0;
}

.status-chip.v-chip {
  height: 28px !important;
  font-size: 0.875rem !important;
  padding: 0 12px !important;
}

.status-chip.v-chip .v-icon {
  font-size: 18px !important;
}

.action-btn.v-btn--icon {
  width: 36px !important;
  height: 36px !important;
}

.action-btn.v-btn--icon .v-icon {
  font-size: 20px !important;
}

.v-pagination {
  justify-content: center;
}

.v-pagination >>> .v-pagination__item {
  min-width: 32px !important;
  height: 32px !important;
  font-size: 0.875rem !important;
}
</style>
